// Lib
import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  User,
} from 'lucide-react';

// Components
import OrgMenu from '@components/AccountMenu/OrgMenu';
import AccountPopover from '@components/AccountMenu/AccountPopover';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@components/ui/avatar';
import { Separator } from '@components/ui/separator';
import { cn } from '@components/lib/utils';

// Hooks
import useUnreadNotificationsCount from '@hooks/useUnreadNotificationsCount';
import useAccountMenu from '@hooks/useAccountMenu';
import useWindowSize from '@hooks/useWindowSize';
import useHasCampaignInvitations from '@modules/Campaigns/hooks/useHasCampaignInvitations';
import useInboxAvailable from '@modules/Inbox/hooks/useInboxAvailable';
import {
  effectiveCollapsed,
  toggleSidebar,
  useLayoutPrefs,
} from '@hooks/layoutPrefs';

// Types
import type { RootState } from '@app-types/index';

import { type NavSection, buildNav } from './navConfig';
import SidebarSection, { type NavBadge } from './SidebarSection';
import MobileNav from './MobileNav';

export { isNavItemActive, isSectionActive, buildNav } from './navConfig';

export const SIDEBAR_EXPANDED_WIDTH = 232;
export const SIDEBAR_COLLAPSED_WIDTH = 72;

const initials = (name?: string): string =>
  (name ?? '').trim().charAt(0).toUpperCase() || '?';

/* Same derivation as `AccountPopover` (`name || username`); with no name at
 * all a person glyph stands in — "?" reads as an error rather than "you". */
const accountName = (profile?: {
  name?: string;
  username?: string;
}): string | undefined => profile?.name || profile?.username;

const accountFallback = (name?: string): React.ReactNode =>
  (name ?? '').trim().charAt(0).toUpperCase() || <User className="size-4" />;

const rowButtonClass = (collapsed: boolean): string =>
  cn(
    'flex min-w-0 cursor-pointer items-center rounded-lg text-left outline-none transition-colors hover:bg-card focus-visible:ring-2 focus-visible:ring-marble-blue-500 dark:hover:bg-marble-bg-100',
    collapsed ? 'justify-center p-1' : 'w-full gap-2.5 p-1.5',
  );

/** Paths that draw their own full-screen chrome. */
const isChromeless = (pathname: string): boolean =>
  pathname.includes('/connect') ||
  pathname.includes('/settings/advanced') ||
  pathname.includes('/settings/appearance') ||
  pathname.includes('/settings/custom') ||
  pathname.includes('/settings/additional-info') ||
  pathname.includes('/settings/role') ||
  pathname.startsWith('/onboarding');

type AccountMenuState = ReturnType<typeof useAccountMenu>;

/** TOP — property switcher (same popover as before, now with the name). */
const PropertyRow: React.FC<{ collapsed: boolean; menu: AccountMenuState }> = ({
  collapsed,
  menu,
}) => {
  const [open, setOpen] = useState(false);
  const { selectedOrganization, invitations } = menu;
  const orgName = selectedOrganization?.profile?.name;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Property: ${orgName || 'Organization'}. Switch property`}
          className={rowButtonClass(collapsed)}
        >
          <span className="relative block shrink-0">
            <Avatar>
              <AvatarImage src={selectedOrganization?.profile?.avatar?.url} />
              <AvatarFallback>{initials(orgName)}</AvatarFallback>
            </Avatar>
            {invitations.length > 0 && (
              <span className="absolute -top-1 -right-1 flex size-5 items-center justify-center rounded-full border-2 border-marble-blue-50 bg-marble-blue-500 text-[10px] font-bold text-white">
                {invitations.length}
              </span>
            )}
          </span>
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                {orgName || 'Organization'}
              </span>
              <ChevronsUpDown
                className="size-4 shrink-0 text-marble-bg-500"
                aria-hidden
              />
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={12}
        className="flex w-64 flex-col gap-0.5 p-2"
      >
        <OrgMenu
          organizations={menu.organizations}
          selectedOrganization={selectedOrganization}
          onCreateOrganization={menu.onCreateOrganization}
          onClose={() => setOpen(false)}
          invitations={invitations}
          busyInvitationId={menu.busyInvitationId}
          onAcceptInvitation={menu.acceptInvitation}
          onDeclineInvitation={menu.declineInvitation}
        />
      </PopoverContent>
    </Popover>
  );
};

/** BOTTOM — account (same popover as before, now with the name). */
const AccountRow: React.FC<{
  collapsed: boolean;
  profile: AccountMenuState['profile'];
}> = ({ collapsed, profile }) => {
  const [open, setOpen] = useState(false);
  const userName = accountName(profile);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Account: ${userName || 'you'}`}
          className={rowButtonClass(collapsed)}
        >
          <Avatar className="shrink-0">
            <AvatarImage src={profile?.avatar?.url} />
            <AvatarFallback>{accountFallback(userName)}</AvatarFallback>
          </Avatar>
          {!collapsed && (
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
              {userName || 'Account'}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="end"
        sideOffset={12}
        className="flex w-64 flex-col gap-0.5 p-2"
      >
        <AccountPopover profile={profile} onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
};

const CollapseToggle: React.FC<{ collapsed: boolean; width: number }> = ({
  collapsed,
  width,
}) => (
  <button
    type="button"
    onClick={() => toggleSidebar(width)}
    aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
    aria-pressed={collapsed}
    className={cn(
      'flex h-9 cursor-pointer items-center rounded-lg text-sm text-marble-bg-500 outline-none hover:bg-card hover:text-foreground focus-visible:ring-2 focus-visible:ring-marble-blue-500 dark:hover:bg-marble-bg-100',
      collapsed ? 'size-11 justify-center' : 'gap-3 px-2.5',
    )}
  >
    {collapsed ? (
      <ChevronsRight className="size-5" aria-hidden />
    ) : (
      <>
        <ChevronsLeft className="size-5" aria-hidden />
        Collapse
      </>
    )}
  </button>
);

/** effectiveCollapsed = preference || (pinned panel pushing && < 1536). */
const useSidebarCollapsed = (width: number): boolean => {
  const prefs = useLayoutPrefs();
  return effectiveCollapsed({
    pref: prefs.sidebarCollapsedPref,
    viewportWidth: width,
    aiPanelPushing: prefs.aiPanelPushing,
    autoOverride: prefs.sidebarAutoOverride,
  });
};

const NavList: React.FC<{
  sections: NavSection[];
  collapsed: boolean;
  badges: Partial<Record<string, NavBadge>>;
}> = ({ sections, collapsed, badges }) => (
  <ul className="flex flex-col gap-0.5">
    {sections.map((section) => (
      <SidebarSection
        key={section.id}
        section={section}
        collapsed={collapsed}
        badge={badges[section.id]}
      />
    ))}
  </ul>
);

const Sidebar: React.FC = () => {
  const { pathname } = useLocation();
  const { width = 1920 } = useWindowSize();
  const menu = useAccountMenu();

  const { isOnboardingPassed, personaType } = useSelector(
    (state: RootState) => ({
      isOnboardingPassed:
        !!(state?.Account?.profile as { onboardingPassed?: boolean } | null)
          ?.onboardingPassed && !!state?.Account?.organizations?.length,
      personaType: state?.SelectedOrganization?.type,
    }),
  );

  const { unreadCount } = useUnreadNotificationsCount(!!isOnboardingPassed);
  const hasCampaignInvitations = useHasCampaignInvitations();
  const hasInbox = useInboxAvailable();
  const collapsed = useSidebarCollapsed(width);

  const nav = useMemo(
    () => buildNav(personaType, hasCampaignInvitations, hasInbox),
    [personaType, hasCampaignInvitations, hasInbox],
  );

  if (!isOnboardingPassed || isChromeless(pathname)) return null;

  const badges: Partial<Record<string, NavBadge>> = {
    notifications: {
      count: pathname.includes('/notifications') ? 0 : unreadCount,
      label: 'unread',
    },
  };

  // A link-in-bio page is full-bleed on mobile.
  const hideMobileBar =
    pathname.startsWith('/link-in-bio/') &&
    pathname.length > '/link-in-bio/'.length;

  return (
    <>
      <aside
        aria-label="Sidebar"
        data-collapsed={collapsed}
        className={cn(
          'sidebar-container sticky top-0 z-10 flex h-screen shrink-0 flex-col bg-marble-blue-50 py-4 transition-[width] duration-200 ease-in-out dark:bg-card max-[1279px]:hidden',
          collapsed ? 'w-[72px] px-2' : 'w-[232px] px-3',
        )}
      >
        <PropertyRow collapsed={collapsed} menu={menu} />

        <nav
          aria-label="Main"
          // Collapsed icons carry hover labels that stick out to the right;
          // a scroll container would clip them, and 9 icons always fit.
          className={cn(
            'mt-4 flex min-h-0 flex-1 flex-col',
            !collapsed && '-mx-3 overflow-y-auto [scrollbar-width:none]',
          )}
        >
          <NavList
            sections={nav.primary}
            collapsed={collapsed}
            badges={badges}
          />
          <Separator
            className={cn('my-3 bg-marble-bg-100', !collapsed && 'mx-3 w-auto')}
          />
          <NavList
            sections={nav.secondary}
            collapsed={collapsed}
            badges={badges}
          />
        </nav>

        <div
          className={cn(
            'mt-2 flex flex-col gap-1',
            collapsed && 'items-center',
          )}
        >
          <CollapseToggle collapsed={collapsed} width={width} />
          <AccountRow collapsed={collapsed} profile={menu.profile} />
        </div>
      </aside>

      <MobileNav
        nav={nav}
        badges={badges}
        hidden={hideMobileBar}
        profile={menu.profile}
        org={menu}
      />
    </>
  );
};

export default Sidebar;
